/**
 * Retention / legal hold, watermarked sharing, invoice kinds and templates,
 * break-glass support access (plan 3.13, 3.14, 3.16).
 */

const DOC_TYPES_EN = {
  "*": "Any type", invoice: "Invoice", contract: "Contract", payroll: "Payroll", personnel: "Personnel file",
  tax: "Tax", accounting: "Accounting record", correspondence: "Correspondence", customs: "Customs",
  quality: "Quality record", other: "Other"
};
const DOC_TYPES_TR = {
  "*": "Tüm türler", invoice: "Fatura", contract: "Sözleşme", payroll: "Bordro", personnel: "Personel dosyası",
  tax: "Vergi", accounting: "Muhasebe kaydı", correspondence: "Yazışma", customs: "Gümrük",
  quality: "Kalite kaydı", other: "Diğer"
};
const DOC_TYPES_AR = {
  "*": "كل الأنواع", invoice: "فاتورة", contract: "عقد", payroll: "كشف رواتب", personnel: "ملف موظف",
  tax: "ضريبة", accounting: "قيد محاسبي", correspondence: "مراسلات", customs: "جمارك",
  quality: "سجل جودة", other: "أخرى"
};

const en = {
  nav: { retention: "Retention & Disposal" },
  common: { copied: "Link copied." },
  files: {
    preview: {
      back: "Back", zoomIn: "Zoom in", zoomOut: "Zoom out",
      pdfUnavailable: "PDF preview is not available for this file.", noOcr: "No OCR text available for this document.",
      officeUnavailable: "Office preview is not available without a download URL.", noText: "No text content available.",
      unsupported: "This file type cannot be previewed"
    },
    share: {
      colEmail: "E-mail", colShared: "Shared",
      watermark: "Watermark when viewed",
      watermarkHint: "Time-limited shares are always watermarked with the recipient's e-mail and the viewing time.",
      watermarkBadge: "Watermark",
      expired: "This share has expired.",
      expiresOn: "Expires {{date}}",
      watermarkNotice: "Shared copy — watermarked: {{text}}",
      watermarkUnsupported: "This file type cannot be shown with a watermark. Ask the owner for a PDF or image copy.",
      watermarkFailed: "The file could not be loaded for watermarking. Try again later."
    }
  },
  retention: {
    title: "Retention & disposal",
    tabs: { policies: "Policies", queue: "Disposal queue", certificates: "Certificates" },
    docTypes: DOC_TYPES_EN,
    fields: {
      docType: "Document type", country: "Country", countryHint: "ISO code, * = any country", years: "Years", months: "Months",
      basis: "Counted from", action: "When expired", note: "Legal basis / note"
    },
    bases: { documentDate: "Document date", createdAt: "Upload date" },
    actions: { destroy: "Queue for disposal", review: "Review only" },
    savePolicy: "Save policy",
    loadTemplates: "Add country templates",
    templatesHint: "Templates are starting points; confirm the periods with your legal advisor before use.",
    noPolicies: "No retention policy yet.",
    remove: "Remove",
    scan: "Find expired documents",
    scanned: "{{count}} document(s) added to the queue.",
    noRequests: "The disposal queue is empty.",
    file: "Document",
    endsAt: "Retention ended",
    status: "Status",
    requestedBy: "Requested by",
    statuses: { pending: "Waiting for approval", approved: "Approved", rejected: "Rejected", cancelled: "Cancelled", destroyed: "Destroyed" },
    approve: "Approve",
    reject: "Reject",
    destroy: "Destroy",
    destroyConfirm: "The document, its versions and stored copies will be deleted permanently and a destruction certificate will be kept. Continue?",
    printCertificate: "Print certificate",
    noCertificates: "No destruction certificate yet.",
    job: "Retention job",
    fourEyes: "A request cannot be approved by the person who opened it.",
    errors: {
      RETENTION_DOCTYPE: "Choose a document type.",
      RETENTION_COUNTRY: "Country must be a two-letter ISO code or *.",
      RETENTION_PERIOD: "Enter a period between 1 month and 100 years.",
      RETENTION_BASIS: "Invalid start date basis.",
      RETENTION_ACTION: "Invalid action.",
      DISPOSAL_SELF_APPROVAL: "You opened this request; another person must approve it.",
      DISPOSAL_NOT_PENDING: "The request is no longer waiting for approval.",
      DISPOSAL_NOT_APPROVED: "The request has not been approved.",
      DISPOSAL_NOT_DUE: "The retention period of this document is not over.",
      LEGAL_HOLD: "The document is on legal hold; the request was cancelled.",
      FILE_NOT_FOUND: "The document no longer exists.",
      LEGAL_HOLD_REASON: "Enter the reason for the legal hold.",
      FOLDER_HAS_LEGAL_HOLD: "The folder contains documents on legal hold and cannot be deleted."
    },
    panel: {
      title: "Retention & legal hold",
      documentDate: "Document date",
      save: "Save",
      saved: "Saved.",
      policy: "Policy",
      noPolicy: "No matching retention policy.",
      endsAt: "Keep until",
      daysLeft: "{{count}} day(s) left",
      expired: "Retention period is over",
      hold: "Legal hold",
      holdActive: "On legal hold — cannot be deleted",
      holdReason: "Reason",
      holdReference: "Case / reference",
      setHold: "Put on legal hold",
      liftHold: "Lift legal hold",
      managersOnly: "Only managers can put or lift a legal hold."
    },
    certificate: {
      title: "Document destruction certificate",
      certificateNo: "Certificate no.", fileName: "Document", fileId: "Record ID", documentType: "Document type",
      country: "Country", policy: "Retention policy", retentionEndsAt: "Retention ended", requestedBy: "Requested by",
      approvedBy: "Approved by", destroyedBy: "Destroyed by", destroyedAt: "Destroyed on", method: "Method",
      objects: "Stored objects deleted", checksum: "Checksum",
      methods: { storage_and_record_delete: "File, versions and stored copies deleted", record_delete: "Record and versions deleted" }
    }
  }
};

const tr = {
  nav: { retention: "Saklama ve İmha" },
  common: { copied: "Bağlantı kopyalandı." },
  files: {
    preview: {
      back: "Geri", zoomIn: "Yakınlaştır", zoomOut: "Uzaklaştır",
      pdfUnavailable: "Bu dosya için PDF önizlemesi yok.", noOcr: "Bu belge için OCR metni yok.",
      officeUnavailable: "İndirme bağlantısı olmadan Office önizlemesi yapılamaz.", noText: "Metin içeriği yok.",
      unsupported: "Bu dosya türü önizlenemez"
    },
    share: {
      colEmail: "E-posta", colShared: "Paylaşıldı",
      watermark: "Görüntülemede filigran",
      watermarkHint: "Süreli paylaşımlar her zaman alıcının e-postası ve görüntüleme zamanıyla filigranlanır.",
      watermarkBadge: "Filigran",
      expired: "Bu paylaşımın süresi doldu.",
      expiresOn: "Bitiş: {{date}}",
      watermarkNotice: "Paylaşılan kopya — filigranlı: {{text}}",
      watermarkUnsupported: "Bu dosya türü filigranla gösterilemiyor. Sahibinden PDF ya da görsel kopya isteyin.",
      watermarkFailed: "Dosya filigran için yüklenemedi. Daha sonra yeniden deneyin."
    }
  },
  retention: {
    title: "Saklama ve imha",
    tabs: { policies: "Politikalar", queue: "İmha kuyruğu", certificates: "Tutanaklar" },
    docTypes: DOC_TYPES_TR,
    fields: {
      docType: "Belge türü", country: "Ülke", countryHint: "ISO kodu, * = tüm ülkeler", years: "Yıl", months: "Ay",
      basis: "Başlangıç", action: "Süre dolunca", note: "Yasal dayanak / not"
    },
    bases: { documentDate: "Belge tarihi", createdAt: "Yükleme tarihi" },
    actions: { destroy: "İmha kuyruğuna al", review: "Yalnızca gözden geçir" },
    savePolicy: "Politikayı kaydet",
    loadTemplates: "Ülke şablonlarını ekle",
    templatesHint: "Şablonlar başlangıç noktasıdır; süreleri kullanmadan önce hukuk danışmanınızla teyit edin.",
    noPolicies: "Henüz saklama politikası yok.",
    remove: "Kaldır",
    scan: "Süresi dolan belgeleri bul",
    scanned: "{{count}} belge kuyruğa eklendi.",
    noRequests: "İmha kuyruğu boş.",
    file: "Belge",
    endsAt: "Saklama bitişi",
    status: "Durum",
    requestedBy: "Talep eden",
    statuses: { pending: "Onay bekliyor", approved: "Onaylandı", rejected: "Reddedildi", cancelled: "İptal edildi", destroyed: "İmha edildi" },
    approve: "Onayla",
    reject: "Reddet",
    destroy: "İmha et",
    destroyConfirm: "Belge, sürümleri ve depolanan kopyaları kalıcı olarak silinecek ve imha tutanağı saklanacak. Devam edilsin mi?",
    printCertificate: "Tutanağı yazdır",
    noCertificates: "Henüz imha tutanağı yok.",
    job: "Saklama işi",
    fourEyes: "Talebi açan kişi onaylayamaz.",
    errors: {
      RETENTION_DOCTYPE: "Belge türü seçin.",
      RETENTION_COUNTRY: "Ülke iki harfli ISO kodu ya da * olmalı.",
      RETENTION_PERIOD: "1 ay ile 100 yıl arasında bir süre girin.",
      RETENTION_BASIS: "Geçersiz başlangıç tarihi.",
      RETENTION_ACTION: "Geçersiz işlem.",
      DISPOSAL_SELF_APPROVAL: "Bu talebi siz açtınız; başka bir kişi onaylamalı.",
      DISPOSAL_NOT_PENDING: "Talep artık onay beklemiyor.",
      DISPOSAL_NOT_APPROVED: "Talep onaylanmadı.",
      DISPOSAL_NOT_DUE: "Bu belgenin saklama süresi dolmadı.",
      LEGAL_HOLD: "Belge yasal saklama altında; talep iptal edildi.",
      FILE_NOT_FOUND: "Belge artık yok.",
      LEGAL_HOLD_REASON: "Yasal saklama gerekçesini girin.",
      FOLDER_HAS_LEGAL_HOLD: "Klasörde yasal saklama altındaki belgeler var; klasör silinemez."
    },
    panel: {
      title: "Saklama ve yasal saklama (legal hold)",
      documentDate: "Belge tarihi",
      save: "Kaydet",
      saved: "Kaydedildi.",
      policy: "Politika",
      noPolicy: "Eşleşen saklama politikası yok.",
      endsAt: "Saklama bitişi",
      daysLeft: "{{count}} gün kaldı",
      expired: "Saklama süresi doldu",
      hold: "Yasal saklama",
      holdActive: "Yasal saklama altında — silinemez",
      holdReason: "Gerekçe",
      holdReference: "Dava / referans",
      setHold: "Yasal saklamaya al",
      liftHold: "Yasal saklamayı kaldır",
      managersOnly: "Yasal saklamayı yalnızca yöneticiler başlatıp kaldırabilir."
    },
    certificate: {
      title: "Belge imha tutanağı",
      certificateNo: "Tutanak no", fileName: "Belge", fileId: "Kayıt kimliği", documentType: "Belge türü",
      country: "Ülke", policy: "Saklama politikası", retentionEndsAt: "Saklama bitişi", requestedBy: "Talep eden",
      approvedBy: "Onaylayan", destroyedBy: "İmha eden", destroyedAt: "İmha tarihi", method: "Yöntem",
      objects: "Silinen depolama nesnesi", checksum: "Sağlama değeri",
      methods: { storage_and_record_delete: "Dosya, sürümler ve depolanan kopyalar silindi", record_delete: "Kayıt ve sürümler silindi" }
    }
  }
};

const ar = {
  nav: { retention: "الاحتفاظ والإتلاف" },
  common: { copied: "تم نسخ الرابط." },
  files: {
    preview: {
      back: "رجوع", zoomIn: "تكبير", zoomOut: "تصغير",
      pdfUnavailable: "معاينة PDF غير متاحة لهذا الملف.", noOcr: "لا يوجد نص OCR لهذا المستند.",
      officeUnavailable: "معاينة Office غير متاحة دون رابط تنزيل.", noText: "لا يوجد محتوى نصي.",
      unsupported: "لا يمكن معاينة هذا النوع من الملفات"
    },
    share: {
      colEmail: "البريد الإلكتروني", colShared: "تمت المشاركة",
      watermark: "علامة مائية عند العرض",
      watermarkHint: "تُوسَم المشاركات محددة المدة دائمًا ببريد المستلم ووقت العرض.",
      watermarkBadge: "علامة مائية",
      expired: "انتهت صلاحية هذه المشاركة.",
      expiresOn: "تنتهي {{date}}",
      watermarkNotice: "نسخة مشتركة — بعلامة مائية: {{text}}",
      watermarkUnsupported: "لا يمكن عرض هذا النوع من الملفات بعلامة مائية. اطلب من المالك نسخة PDF أو صورة.",
      watermarkFailed: "تعذّر تحميل الملف لوضع العلامة المائية. حاول لاحقًا."
    }
  },
  retention: {
    title: "الاحتفاظ والإتلاف",
    tabs: { policies: "السياسات", queue: "قائمة الإتلاف", certificates: "المحاضر" },
    docTypes: DOC_TYPES_AR,
    fields: {
      docType: "نوع المستند", country: "الدولة", countryHint: "رمز ISO، * = كل الدول", years: "سنوات", months: "أشهر",
      basis: "يُحتسب من", action: "عند الانتهاء", note: "السند القانوني / ملاحظة"
    },
    bases: { documentDate: "تاريخ المستند", createdAt: "تاريخ الرفع" },
    actions: { destroy: "إضافة إلى قائمة الإتلاف", review: "مراجعة فقط" },
    savePolicy: "حفظ السياسة",
    loadTemplates: "إضافة قوالب الدول",
    templatesHint: "القوالب نقطة بداية؛ أكّد المدد مع مستشارك القانوني قبل الاستخدام.",
    noPolicies: "لا توجد سياسة احتفاظ بعد.",
    remove: "إزالة",
    scan: "البحث عن المستندات المنتهية",
    scanned: "أُضيف {{count}} مستند إلى القائمة.",
    noRequests: "قائمة الإتلاف فارغة.",
    file: "المستند",
    endsAt: "انتهاء الاحتفاظ",
    status: "الحالة",
    requestedBy: "مقدم الطلب",
    statuses: { pending: "بانتظار الموافقة", approved: "تمت الموافقة", rejected: "مرفوض", cancelled: "ملغى", destroyed: "تم الإتلاف" },
    approve: "موافقة",
    reject: "رفض",
    destroy: "إتلاف",
    destroyConfirm: "سيُحذف المستند وإصداراته ونسخه المخزنة نهائيًا وسيُحفظ محضر إتلاف. متابعة؟",
    printCertificate: "طباعة المحضر",
    noCertificates: "لا يوجد محضر إتلاف بعد.",
    job: "مهمة الاحتفاظ",
    fourEyes: "لا يمكن لمقدم الطلب الموافقة عليه.",
    errors: {
      RETENTION_DOCTYPE: "اختر نوع المستند.",
      RETENTION_COUNTRY: "يجب أن تكون الدولة رمز ISO من حرفين أو *.",
      RETENTION_PERIOD: "أدخل مدة بين شهر و100 سنة.",
      RETENTION_BASIS: "أساس احتساب غير صالح.",
      RETENTION_ACTION: "إجراء غير صالح.",
      DISPOSAL_SELF_APPROVAL: "أنت من فتح هذا الطلب؛ يجب أن يوافق عليه شخص آخر.",
      DISPOSAL_NOT_PENDING: "الطلب لم يعد بانتظار الموافقة.",
      DISPOSAL_NOT_APPROVED: "لم تتم الموافقة على الطلب.",
      DISPOSAL_NOT_DUE: "لم تنتهِ مدة الاحتفاظ بهذا المستند.",
      LEGAL_HOLD: "المستند تحت الحجز القانوني؛ أُلغي الطلب.",
      FILE_NOT_FOUND: "المستند لم يعد موجودًا.",
      LEGAL_HOLD_REASON: "أدخل سبب الحجز القانوني.",
      FOLDER_HAS_LEGAL_HOLD: "يحتوي المجلد على مستندات تحت الحجز القانوني ولا يمكن حذفه."
    },
    panel: {
      title: "الاحتفاظ والحجز القانوني",
      documentDate: "تاريخ المستند",
      save: "حفظ",
      saved: "تم الحفظ.",
      policy: "السياسة",
      noPolicy: "لا توجد سياسة احتفاظ مطابقة.",
      endsAt: "الاحتفاظ حتى",
      daysLeft: "متبقٍ {{count}} يوم",
      expired: "انتهت مدة الاحتفاظ",
      hold: "الحجز القانوني",
      holdActive: "تحت الحجز القانوني — لا يمكن حذفه",
      holdReason: "السبب",
      holdReference: "القضية / المرجع",
      setHold: "وضع حجز قانوني",
      liftHold: "رفع الحجز القانوني",
      managersOnly: "يضع المديرون فقط الحجز القانوني أو يرفعونه."
    },
    certificate: {
      title: "محضر إتلاف مستند",
      certificateNo: "رقم المحضر", fileName: "المستند", fileId: "معرّف السجل", documentType: "نوع المستند",
      country: "الدولة", policy: "سياسة الاحتفاظ", retentionEndsAt: "انتهاء الاحتفاظ", requestedBy: "مقدم الطلب",
      approvedBy: "الموافق", destroyedBy: "المُتلِف", destroyedAt: "تاريخ الإتلاف", method: "الطريقة",
      objects: "الكائنات المخزنة المحذوفة", checksum: "قيمة التحقق",
      methods: { storage_and_record_delete: "حُذف الملف والإصدارات والنسخ المخزنة", record_delete: "حُذف السجل والإصدارات" }
    }
  }
};

const INVOICE_KINDS_EN = {
  kind: "Invoice kind",
  kinds: { standard: "Standard", creditNote: "Credit note / return", advance: "Advance", progress: "Progress billing" },
  language: "Document language",
  languageHint: "The printed invoice uses the buyer's language.",
  languages: { tr: "Türkçe", en: "English", ar: "العربية" },
  originalInvoice: "Original invoice",
  selectOriginal: "Select the invoice to credit",
  remaining: "Not yet credited: {{amount}}",
  creditReason: "Reason for the credit",
  contractRef: "Contract reference",
  contractAmount: "Contract value (excl. tax)",
  cumulativePct: "Cumulative progress %",
  previous: "Billed before: {{pct}}% (this is progress no. {{number}})",
  retentionPct: "Retention %",
  progressTax: "Tax rate",
  periodAmount: "This period (excl. tax)",
  progressLine: "Progress billing no. {{number}} — {{contract}}",
  retention: "Retention",
  advances: "Deduct advances",
  advanceOption: "{{no}} — remaining {{amount}}",
  noAdvances: "No open advance invoice in this currency.",
  deductions: "Advances deducted",
  payable: "Amount due",
  print: "Print / save as PDF",
  printBlocked: "The print window was blocked. Allow pop-ups and try again.",
  errors: {
    KIND_INVALID: "Unknown invoice kind.",
    LANGUAGE_INVALID: "Unsupported document language.",
    ORIGINAL_REQUIRED: "Select the original invoice.",
    ORIGINAL_IS_CREDIT: "A credit note cannot be credited.",
    CURRENCY_MISMATCH: "Currency must match the linked invoice.",
    CREDIT_REASON_REQUIRED: "Enter the reason for the credit.",
    CREDIT_EXCEEDS_ORIGINAL: "The credit is larger than what is left on the original invoice.",
    DEDUCTION_NOT_ALLOWED: "This kind of invoice cannot deduct advances.",
    CONTRACT_REQUIRED: "Enter the contract reference and value.",
    PROGRESS_PCT_INVALID: "Cumulative progress must be above the previous one and at most 100%.",
    RETENTION_PCT_INVALID: "Retention must be between 0 and 100%.",
    ADVANCE_NOT_FOUND: "The advance invoice was not found.",
    ADVANCE_EXCEEDS: "The deduction is larger than the remaining advance.",
    DEDUCTIONS_EXCEED_TOTAL: "Deductions are larger than the invoice total.",
    INVOICE_REFERENCED: "Other invoices refer to this one (credit note or advance deduction). Cancel them first."
  }
};

const INVOICE_KINDS_TR = {
  kind: "Fatura türü",
  kinds: { standard: "Standart", creditNote: "İade / alacak dekontu", advance: "Avans", progress: "Hakediş" },
  language: "Belge dili",
  languageHint: "Yazdırılan fatura alıcının dilinde olur.",
  languages: { tr: "Türkçe", en: "English", ar: "العربية" },
  originalInvoice: "İlgili fatura",
  selectOriginal: "İade edilecek faturayı seçin",
  remaining: "İade edilmemiş tutar: {{amount}}",
  creditReason: "İade / düzeltme nedeni",
  contractRef: "Sözleşme referansı",
  contractAmount: "Sözleşme bedeli (KDV hariç)",
  cumulativePct: "Kümülatif ilerleme %",
  previous: "Önceden faturalanan: %{{pct}} (bu {{number}}. hakediş)",
  retentionPct: "Teminat kesintisi %",
  progressTax: "KDV oranı",
  periodAmount: "Bu dönem (KDV hariç)",
  progressLine: "{{number}} no.lu hakediş — {{contract}}",
  retention: "Teminat kesintisi",
  advances: "Avans mahsubu",
  advanceOption: "{{no}} — kalan {{amount}}",
  noAdvances: "Bu para biriminde açık avans faturası yok.",
  deductions: "Mahsup edilen avans",
  payable: "Ödenecek tutar",
  print: "Yazdır / PDF olarak kaydet",
  printBlocked: "Yazdırma penceresi engellendi. Açılır pencerelere izin verip tekrar deneyin.",
  errors: {
    KIND_INVALID: "Bilinmeyen fatura türü.",
    LANGUAGE_INVALID: "Desteklenmeyen belge dili.",
    ORIGINAL_REQUIRED: "İlgili faturayı seçin.",
    ORIGINAL_IS_CREDIT: "İade faturası tekrar iade edilemez.",
    CURRENCY_MISMATCH: "Para birimi bağlı faturayla aynı olmalı.",
    CREDIT_REASON_REQUIRED: "İade nedenini girin.",
    CREDIT_EXCEEDS_ORIGINAL: "İade tutarı, ilgili faturada kalan tutarı aşıyor.",
    DEDUCTION_NOT_ALLOWED: "Bu fatura türünde avans mahsubu yapılamaz.",
    CONTRACT_REQUIRED: "Sözleşme referansını ve bedelini girin.",
    PROGRESS_PCT_INVALID: "Kümülatif ilerleme bir öncekinden büyük ve en fazla %100 olmalı.",
    RETENTION_PCT_INVALID: "Teminat kesintisi %0 ile %100 arasında olmalı.",
    ADVANCE_NOT_FOUND: "Avans faturası bulunamadı.",
    ADVANCE_EXCEEDS: "Mahsup tutarı kalan avansı aşıyor.",
    DEDUCTIONS_EXCEED_TOTAL: "Kesintiler fatura toplamını aşıyor.",
    INVOICE_REFERENCED: "Bu faturaya bağlı başka faturalar var (iade veya avans mahsubu). Önce onları iptal edin."
  }
};

const INVOICE_KINDS_AR = {
  kind: "نوع الفاتورة",
  kinds: { standard: "عادية", creditNote: "إشعار دائن / مرتجع", advance: "دفعة مقدمة", progress: "مستخلص" },
  language: "لغة المستند",
  languageHint: "تُطبع الفاتورة بلغة المشتري.",
  languages: { tr: "Türkçe", en: "English", ar: "العربية" },
  originalInvoice: "الفاتورة الأصلية",
  selectOriginal: "اختر الفاتورة المراد إصدار إشعار دائن لها",
  remaining: "غير مُشعر به بعد: {{amount}}",
  creditReason: "سبب الإشعار",
  contractRef: "مرجع العقد",
  contractAmount: "قيمة العقد (بدون ضريبة)",
  cumulativePct: "نسبة الإنجاز التراكمية %",
  previous: "المفوتر سابقًا: {{pct}}% (هذا المستخلص رقم {{number}})",
  retentionPct: "ضمان حسن التنفيذ %",
  progressTax: "نسبة الضريبة",
  periodAmount: "هذه الفترة (بدون ضريبة)",
  progressLine: "المستخلص رقم {{number}} — {{contract}}",
  retention: "ضمان حسن التنفيذ",
  advances: "خصم الدفعات المقدمة",
  advanceOption: "{{no}} — المتبقي {{amount}}",
  noAdvances: "لا توجد فاتورة دفعة مقدمة مفتوحة بهذه العملة.",
  deductions: "الدفعات المقدمة المخصومة",
  payable: "المبلغ المستحق",
  print: "طباعة / حفظ PDF",
  printBlocked: "تم حظر نافذة الطباعة. اسمح بالنوافذ المنبثقة وحاول مرة أخرى.",
  errors: {
    KIND_INVALID: "نوع فاتورة غير معروف.",
    LANGUAGE_INVALID: "لغة المستند غير مدعومة.",
    ORIGINAL_REQUIRED: "اختر الفاتورة الأصلية.",
    ORIGINAL_IS_CREDIT: "لا يمكن إصدار إشعار دائن لإشعار دائن.",
    CURRENCY_MISMATCH: "يجب أن تطابق العملة الفاتورة المرتبطة.",
    CREDIT_REASON_REQUIRED: "أدخل سبب الإشعار.",
    CREDIT_EXCEEDS_ORIGINAL: "مبلغ الإشعار أكبر من المتبقي في الفاتورة الأصلية.",
    DEDUCTION_NOT_ALLOWED: "لا يمكن خصم الدفعات المقدمة في هذا النوع من الفواتير.",
    CONTRACT_REQUIRED: "أدخل مرجع العقد وقيمته.",
    PROGRESS_PCT_INVALID: "يجب أن تكون النسبة التراكمية أكبر من السابقة وألا تتجاوز 100%.",
    RETENTION_PCT_INVALID: "يجب أن يكون ضمان حسن التنفيذ بين 0 و100%.",
    ADVANCE_NOT_FOUND: "لم يتم العثور على فاتورة الدفعة المقدمة.",
    ADVANCE_EXCEEDS: "مبلغ الخصم أكبر من المتبقي من الدفعة المقدمة.",
    DEDUCTIONS_EXCEED_TOTAL: "الخصومات أكبر من إجمالي الفاتورة.",
    INVOICE_REFERENCED: "توجد فواتير أخرى مرتبطة بهذه الفاتورة (إشعار دائن أو خصم دفعة مقدمة). ألغِها أولًا."
  }
};

const SCOPES_EN = {
  files: "Files", folders: "Folders", invoices: "Invoices", accounts: "Accounts", transactions: "Transactions",
  products: "Products", stockMovements: "Stock movements", warehouses: "Warehouses", customers: "Customers",
  salesOrders: "Sales orders", purchaseRequests: "Purchase requests", entityDocuments: "Linked documents", statusHistory: "Status history"
};
const SCOPES_TR = {
  files: "Dosyalar", folders: "Klasörler", invoices: "Faturalar", accounts: "Cariler", transactions: "Hareketler",
  products: "Ürünler", stockMovements: "Stok hareketleri", warehouses: "Depolar", customers: "Müşteriler",
  salesOrders: "Satış siparişleri", purchaseRequests: "Satın alma talepleri", entityDocuments: "Bağlı belgeler", statusHistory: "Durum geçmişi"
};
const SCOPES_AR = {
  files: "الملفات", folders: "المجلدات", invoices: "الفواتير", accounts: "الحسابات", transactions: "الحركات",
  products: "المنتجات", stockMovements: "حركات المخزون", warehouses: "المستودعات", customers: "العملاء",
  salesOrders: "أوامر البيع", purchaseRequests: "طلبات الشراء", entityDocuments: "المستندات المرتبطة", statusHistory: "سجل الحالة"
};

const ACCESS_EN = {
  title: "Temporary support access",
  intro: "Read-only access to the customer's data for this ticket, approved by the customer, for at most 4 hours.",
  scope: "Data to view", duration: "Duration", hours: "{{count}} hour(s)", reason: "Why is access needed?",
  reasonHint: "At least 10 characters. The customer sees this.", request: "Request access",
  waiting: "Waiting for the customer's approval.", cancel: "Withdraw request",
  approvePrompt: "{{name}} asks for read-only access to: {{scope}} for {{count}} hour(s).",
  approve: "Approve", reject: "Reject", activeFor: "Active — {{minutes}} min left", open: "Open read-only view", revoke: "End access now",
  statuses: { requested: "Requested", active: "Active", rejected: "Rejected", revoked: "Ended", expired: "Expired" },
  log: "Access log",
  eventTypes: { requested: "Requested", approved: "Approved", rejected: "Rejected", used: "Viewed", revoked: "Ended", expired: "Expired" },
  usedDetail: "{{count}} record(s) in {{collection}}",
  scopes: SCOPES_EN,
  errors: {
    TICKET_CLOSED: "The ticket is closed.", NOT_ASSIGNEE: "Only the person handling the ticket can ask for access.",
    SELF_ACCESS: "You cannot ask for access to your own data.", SCOPE_INVALID: "Pick at least one kind of data.",
    DURATION_INVALID: "Duration must be 1 to 4 hours.", REASON_TOO_SHORT: "Explain the reason in at least 10 characters.",
    GRANT_OPEN: "There is already an open request.", GRANT_CANNOT_APPROVE: "You cannot approve or reject this request.",
    GRANT_NOT_OPEN: "This access is no longer open.", GRANT_NOT_ACTIVE: "Access is not active.",
    GRANT_NOT_YOURS: "This access belongs to someone else.", GRANT_NOT_FOUND: "Access request not found."
  },
  viewer: {
    title: "Read-only support view",
    banner: "Ticket {{ticket}} · ends in {{minutes}} min. Every view is logged and visible to the customer.",
    collection: "Data", load: "Show", empty: "No records.", notActive: "This access is not active.",
    colId: "Record", colLabel: "Name / number", colUpdated: "Last updated", back: "Back to ticket"
  }
};
const ACCESS_TR = {
  title: "Geçici destek erişimi",
  intro: "Bu talep için müşterinin onayladığı, en çok 4 saatlik, salt okunur veri erişimi.",
  scope: "Görülecek veri", duration: "Süre", hours: "{{count}} saat", reason: "Erişim neden gerekli?",
  reasonHint: "En az 10 karakter. Müşteri bu açıklamayı görür.", request: "Erişim iste",
  waiting: "Müşteri onayı bekleniyor.", cancel: "Talebi geri çek",
  approvePrompt: "{{name}}, {{count}} saatliğine şu verilere salt okunur erişim istiyor: {{scope}}.",
  approve: "Onayla", reject: "Reddet", activeFor: "Etkin — {{minutes}} dk kaldı", open: "Salt okunur görünümü aç", revoke: "Erişimi şimdi bitir",
  statuses: { requested: "İstendi", active: "Etkin", rejected: "Reddedildi", revoked: "Sonlandırıldı", expired: "Süresi doldu" },
  log: "Erişim günlüğü",
  eventTypes: { requested: "İstendi", approved: "Onaylandı", rejected: "Reddedildi", used: "Görüntülendi", revoked: "Sonlandırıldı", expired: "Süresi doldu" },
  usedDetail: "{{collection}}: {{count}} kayıt",
  scopes: SCOPES_TR,
  errors: {
    TICKET_CLOSED: "Talep kapalı.", NOT_ASSIGNEE: "Erişimi yalnızca talebi yürüten kişi isteyebilir.",
    SELF_ACCESS: "Kendi verinize erişim isteyemezsiniz.", SCOPE_INVALID: "En az bir veri türü seçin.",
    DURATION_INVALID: "Süre 1 ile 4 saat arasında olmalı.", REASON_TOO_SHORT: "Nedeni en az 10 karakterle açıklayın.",
    GRANT_OPEN: "Zaten açık bir talep var.", GRANT_CANNOT_APPROVE: "Bu talebi onaylayamaz veya reddedemezsiniz.",
    GRANT_NOT_OPEN: "Bu erişim artık açık değil.", GRANT_NOT_ACTIVE: "Erişim etkin değil.",
    GRANT_NOT_YOURS: "Bu erişim başka birine ait.", GRANT_NOT_FOUND: "Erişim talebi bulunamadı."
  },
  viewer: {
    title: "Salt okunur destek görünümü",
    banner: "{{ticket}} numaralı talep · {{minutes}} dk sonra kapanır. Her görüntüleme kaydedilir ve müşteri tarafından görülür.",
    collection: "Veri", load: "Göster", empty: "Kayıt yok.", notActive: "Bu erişim etkin değil.",
    colId: "Kayıt", colLabel: "Ad / numara", colUpdated: "Son güncelleme", back: "Talebe dön"
  }
};
const ACCESS_AR = {
  title: "وصول دعم مؤقت",
  intro: "وصول للقراءة فقط إلى بيانات العميل لهذه التذكرة، بموافقة العميل، لمدة أقصاها 4 ساعات.",
  scope: "البيانات المطلوب عرضها", duration: "المدة", hours: "{{count}} ساعة", reason: "لماذا يلزم الوصول؟",
  reasonHint: "10 أحرف على الأقل. يرى العميل هذا النص.", request: "طلب الوصول",
  waiting: "بانتظار موافقة العميل.", cancel: "سحب الطلب",
  approvePrompt: "يطلب {{name}} وصولًا للقراءة فقط إلى: {{scope}} لمدة {{count}} ساعة.",
  approve: "موافقة", reject: "رفض", activeFor: "نشط — متبقٍ {{minutes}} دقيقة", open: "فتح العرض للقراءة فقط", revoke: "إنهاء الوصول الآن",
  statuses: { requested: "مطلوب", active: "نشط", rejected: "مرفوض", revoked: "منتهٍ", expired: "منتهي الصلاحية" },
  log: "سجل الوصول",
  eventTypes: { requested: "طُلب", approved: "تمت الموافقة", rejected: "رُفض", used: "عُرض", revoked: "أُنهي", expired: "انتهت الصلاحية" },
  usedDetail: "{{count}} سجل في {{collection}}",
  scopes: SCOPES_AR,
  errors: {
    TICKET_CLOSED: "التذكرة مغلقة.", NOT_ASSIGNEE: "يطلب الوصول فقط من يتولى التذكرة.",
    SELF_ACCESS: "لا يمكنك طلب الوصول إلى بياناتك.", SCOPE_INVALID: "اختر نوع بيانات واحدًا على الأقل.",
    DURATION_INVALID: "يجب أن تكون المدة بين 1 و4 ساعات.", REASON_TOO_SHORT: "اشرح السبب في 10 أحرف على الأقل.",
    GRANT_OPEN: "يوجد طلب مفتوح بالفعل.", GRANT_CANNOT_APPROVE: "لا يمكنك الموافقة على هذا الطلب أو رفضه.",
    GRANT_NOT_OPEN: "هذا الوصول لم يعد مفتوحًا.", GRANT_NOT_ACTIVE: "الوصول غير نشط.",
    GRANT_NOT_YOURS: "هذا الوصول يخص شخصًا آخر.", GRANT_NOT_FOUND: "لم يتم العثور على طلب الوصول."
  },
  viewer: {
    title: "عرض الدعم للقراءة فقط",
    banner: "التذكرة {{ticket}} · ينتهي خلال {{minutes}} دقيقة. يُسجَّل كل عرض ويطّلع عليه العميل.",
    collection: "البيانات", load: "عرض", empty: "لا توجد سجلات.", notActive: "هذا الوصول غير نشط.",
    colId: "السجل", colLabel: "الاسم / الرقم", colUpdated: "آخر تحديث", back: "العودة إلى التذكرة"
  }
};

const A11Y_EN = {
  skipToContent: "Skip to main content",
  openDetails: "Open details", toggleNavigation: "Toggle navigation", listView: "List view", gridView: "Grid view",
  filterStatus: "Filter by status", filterType: "Filter by type", filterCategory: "Filter by category",
  filterBalance: "Filter by balance", filterProduct: "Filter by product", filterWarehouse: "Filter by warehouse",
  quantity: "Quantity", unitPrice: "Unit price", taxRate: "Tax rate", minQty: "Minimum", maxQty: "Maximum",
  uomCode: "Unit of measure", location: "Location", product: "Product", priority: "Priority", status: "Status",
  lotNumber: "Lot number", code: "Code", role: "Role", selectRow: "Select row", recount: "Recount",
  compareSlider: "Comparison position", recognizedText: "Recognized text", pickingRule: "Picking rule",
  includeDocument: "Include document",
  progress: {
    upload: "Upload progress", import: "Import progress", storage: "Storage used", quota: "Quota used",
    passwordStrength: "Password strength", capacity: "Capacity used", confidence: "Confidence", ocr: "Text recognized"
  }
};
const A11Y_TR = {
  skipToContent: "Ana içeriğe geç",
  openDetails: "Ayrıntıları aç", toggleNavigation: "Menüyü aç/kapat", listView: "Liste görünümü", gridView: "Izgara görünümü",
  filterStatus: "Duruma göre süz", filterType: "Türe göre süz", filterCategory: "Kategoriye göre süz",
  filterBalance: "Bakiyeye göre süz", filterProduct: "Ürüne göre süz", filterWarehouse: "Depoya göre süz",
  quantity: "Miktar", unitPrice: "Birim fiyat", taxRate: "Vergi oranı", minQty: "Asgari", maxQty: "Azami",
  uomCode: "Ölçü birimi", location: "Lokasyon", product: "Ürün", priority: "Öncelik", status: "Durum",
  lotNumber: "Lot numarası", code: "Kod", role: "Rol", selectRow: "Satırı seç", recount: "Yeniden say",
  compareSlider: "Karşılaştırma konumu", recognizedText: "Tanınan metin", pickingRule: "Toplama kuralı",
  includeDocument: "Belgeyi ekle",
  progress: {
    upload: "Yükleme ilerlemesi", import: "İçe aktarma ilerlemesi", storage: "Kullanılan depolama", quota: "Kullanılan kota",
    passwordStrength: "Parola gücü", capacity: "Kullanılan kapasite", confidence: "Güven düzeyi", ocr: "Tanınan metin oranı"
  }
};
const A11Y_AR = {
  skipToContent: "انتقل إلى المحتوى الرئيسي",
  openDetails: "فتح التفاصيل", toggleNavigation: "تبديل القائمة", listView: "عرض القائمة", gridView: "عرض الشبكة",
  filterStatus: "تصفية حسب الحالة", filterType: "تصفية حسب النوع", filterCategory: "تصفية حسب الفئة",
  filterBalance: "تصفية حسب الرصيد", filterProduct: "تصفية حسب المنتج", filterWarehouse: "تصفية حسب المستودع",
  quantity: "الكمية", unitPrice: "سعر الوحدة", taxRate: "نسبة الضريبة", minQty: "الحد الأدنى", maxQty: "الحد الأقصى",
  uomCode: "وحدة القياس", location: "الموقع", product: "المنتج", priority: "الأولوية", status: "الحالة",
  lotNumber: "رقم الدفعة", code: "الرمز", role: "الدور", selectRow: "تحديد الصف", recount: "إعادة العد",
  compareSlider: "موضع المقارنة", recognizedText: "النص المتعرف عليه", pickingRule: "قاعدة الانتقاء",
  includeDocument: "تضمين المستند",
  progress: {
    upload: "تقدم الرفع", import: "تقدم الاستيراد", storage: "التخزين المستخدم", quota: "الحصة المستخدمة",
    passwordStrength: "قوة كلمة المرور", capacity: "السعة المستخدمة", confidence: "مستوى الثقة", ocr: "النص المتعرف عليه"
  }
};

export const complianceLocales = {
  en: { ...en, a11y: A11Y_EN, accounting: { invoiceKinds: INVOICE_KINDS_EN }, support: { access: ACCESS_EN } },
  tr: { ...tr, a11y: A11Y_TR, accounting: { invoiceKinds: INVOICE_KINDS_TR }, support: { access: ACCESS_TR } },
  ar: { ...ar, a11y: A11Y_AR, accounting: { invoiceKinds: INVOICE_KINDS_AR }, support: { access: ACCESS_AR } }
};
